import React from "react";
import { makeStyles } from "tss-react/mui";

import { Box, Typography } from "@mui/material";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

const useStyles = makeStyles()((theme) => ({
    root: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: 560,
        margin: "0 auto",
        padding: theme.spacing(8, 4),
        textAlign: "center",
        // Table cells do not wrap their text, and this often sits inside one.
        whiteSpace: "normal",
        boxSizing: "border-box",
        "&::after": {
            content: '""',
            flex: "1 1 0px",
        },
    },
    frame: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
    },
    rootCompact: {
        padding: theme.spacing(4, 2),
    },
    imageArea: {
        flex: "1 1 0px",
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingBottom: theme.spacing(2),
    },
    image: {
        display: "block",
        width: "100%",
        maxWidth: 520,
        height: "auto",
        aspectRatio: "2 / 1",
        objectFit: "cover",
    },
    imageCompact: {
        maxWidth: 300,
    },
    body: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    title: {
        color: theme.palette.emteria.primary,
    },
    description: {
        color: theme.palette.emteria.text.secondary,
    },
    action: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
}));

type EmptyStateProps = {
    image: string;
    title: string;
    description: React.ReactNode;
    action?: React.ReactNode;
    size?: "page" | "compact";

    framed?: boolean;
};

export const EmptyState = ({ image, title, description, action, size = "page", framed = false }: EmptyStateProps) => {
    const { classes, cx } = useStyles();
    const isCompact = size === "compact";

    const content = (
        <Box className={cx(classes.root, isCompact && classes.rootCompact)} data-testid="empty-state">
            <Box className={classes.imageArea}>
                <img src={image} alt="" className={cx(classes.image, isCompact && classes.imageCompact)} />
            </Box>
            <Box className={classes.body}>
                <Typography component="h2" variant={isCompact ? "subtitle1" : "h6"} className={classes.title}>
                    {title}
                </Typography>
                <Typography variant={isCompact ? "body2" : "body1"} className={classes.description}>
                    {description}
                </Typography>
                {action ? <Box className={classes.action}>{action}</Box> : null}
            </Box>
        </Box>
    );

    return framed ? <SectionPanel className={classes.frame}>{content}</SectionPanel> : content;
};
